import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  Alert,
  Grid,
  TextField,
  Chip,
  LinearProgress,
  CircularProgress,
  MenuItem,
  Select,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
  Fingerprint as FingerprintIcon,
  SettingsInputAntenna as SettingsInputAntennaIcon,
  Badge as BadgeIcon,
  Grade as GradeIcon,
  HighlightOff as HighlightOffIcon,
  Person,
  Devices,
} from "@mui/icons-material";
import Modal from "@mui/material/Modal";
import {
  CaptureFingerPrintAeps2,
  CaptureFingerPrintDmt3,
  GetMFS100InfoLoad,
} from "../../utils/MantraCapture";

const AEPSMERCHANTBIOMETRICKYCIPAY = ({
  open,
  onClose,
  title,
  formData,
  setFormData,
  banks = [],
  buttons = [],
  fingerData,
  aadhaar,
  setAadhaar,
  onFingerSuccess,
  type,
  pidWadth,
}) => {
  const [rdDeviceList, setRdDeviceList] = useState([]);
  const [rdDevice, setRdDevice] = useState(null);
  const [status, setStatus] = useState("NOT READY");
  const [scanQuality, setScanQuality] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const renderButtons = () => {
    return buttons.map((btn, index) => (
      <Button
        key={index}
        variant={btn.variant || "contained"}
        onClick={btn.onClick}
        disabled={btn.disabled}
        startIcon={btn.icon}
        sx={{
          background:
            btn.variant === "outlined"
              ? "transparent"
              : btn.bgcolor || "#1976d2",
          color: btn.variant === "outlined" ? "#1976d2" : btn.color || "white",
          borderColor: btn.variant === "outlined" ? "#1976d2" : "transparent",
          borderWidth: btn.variant === "outlined" ? 1 : 0,
          "&:hover": {
            background:
              btn.variant === "outlined"
                ? "rgba(25, 118, 210, 0.04)"
                : "#1565c0",
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
            transform: "translateY(-1px)",
          },
          borderRadius: "8px",
          px: 3,
          py: 1,
          textTransform: "none",
          fontWeight: "600",
          fontSize: { xs: "0.75rem", sm: "0.85rem" },
          boxShadow:
            btn.variant === "outlined"
              ? "none"
              : "0 2px 8px rgba(25, 118, 210, 0.2)",
          transition: "all 0.3s ease",
          minWidth: { xs: "120px", sm: "140px" },
          whiteSpace: "nowrap",
          ...btn.sx,
        }}
      >
        {btn.label}
      </Button>
    ));
  };

  useEffect(() => {
    if (open) {
      detectDevice();
    }
  }, [open]);

  const detectDevice = () => {
    setLoading(true);
    setError("");
    setRdDeviceList([]);
    setRdDevice(null);
    setStatus("DETECTING...");

    GetMFS100InfoLoad(
      setLoading,
      (devices) => {
        setLoading(false);
        if (devices && devices.length > 0) {
          setRdDeviceList(devices);

          const readyDevice = devices.find((d) => d.status === "READY");
          if (readyDevice) {
            setRdDevice(readyDevice);
            setStatus(readyDevice.status);
          } else {
            setStatus("NOT READY");
          }
        } else {
          setError("No fingerprint devices detected");
          setStatus("NOT READY");
        }
      },
      (error) => {
        setLoading(false);
        setError("Failed to detect devices: " + error);
        setStatus("NOT READY");
      }
    );
  };

  const startScan = () => {
    if (!rdDevice) {
      setError("Please select a device first");
      return;
    }

    if (!aadhaar || aadhaar.length !== 12) {
      setError("Please enter a valid 12-digit Aadhaar number");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setStatus("SCANNING...");

    CaptureFingerPrintAeps2(
      rdDevice.rdport,
      (qualityMessage, data) => {
        setLoading(false);
        setStatus("CONNECTED");
        setScanQuality(data.qScore);
        setSuccess(`Fingerprint captured successfully! ${qualityMessage}`);

        fingerData(data);
        if (onFingerSuccess) {
          onFingerSuccess(data);
        }
      },
      (error) => {
        setLoading(false);
        setStatus("ERROR");
        setError("Scan failed: " + error);
      },
      pidWadth
    );
  };

  // Status color logic with blue theme
  const getStatusColor = () => {
    if (status === "CONNECTED") return "#4caf50";
    if (status === "SCANNING...") return "#ff9800";
    if (status === "READY") return "#1976d2";
    if (status === "DETECTING...") return "#2196f3";
    if (status === "ERROR") return "#f44336";
    return "#757575";
  };

  const statusPercentage = (status) => {
    switch (status) {
      case "DETECTING...":
        return 10;
      case "READY":
        return 50;
      case "SCANNING...":
        return 75;
      case "CONNECTED":
        return 100;
      case "ERROR":
        return 100;
      default:
        return 0;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose} 
      aria-labelledby="biometric-kyc-modal"
      aria-describedby="biometric-kyc-description"
      closeAfterTransition
      disableEscapeKeyDown={false} // ✅ Ensure ESC key works
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "85%", md: "75%", lg: "800px" },
          maxWidth: "900px",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(25, 118, 210, 0.3)",
          p: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with blue gradient */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            p: { xs: 2, sm: 3 },
            borderRadius: "12px 12px 0 0",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.25rem" },
                mb: 0.5,
              }}
            >
              {title || "Biometric KYC Verification"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontWeight: "500",
                fontSize: { xs: "0.7rem", sm: "0.85rem" },
                display: "flex",
                alignItems: "center",
              }}
            >
              <FingerprintIcon sx={{ fontSize: "16px", mr: 0.5 }} />
              Secure biometric verification
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              color: "white",
              opacity: 0.8,
              transition: "all 0.2s",
              "&:hover": {
                opacity: 1,
                background: "rgba(255, 255, 255, 0.15)",
              },
              p: 0.5,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflow: "auto" }}>
          {/* Buttons at top - Responsive */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 3 }}
            flexWrap="wrap"
          >
            {renderButtons()}
          </Stack>

          {/* Error/Success Alerts */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                alignItems: "center",
                py: 1,
              }}
              icon={<ErrorOutlineIcon fontSize="small" />}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {error}
              </Typography>
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 2,
                alignItems: "center",
                py: 1,
              }}
              icon={<CheckCircleOutlineIcon fontSize="small" />}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {success}
              </Typography>
            </Alert>
          )}

          {/* Main Content Grid */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Left Side - Device Status */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "#f8fbff",
                  borderRadius: 2,
                  p: { xs: 2, sm: 2.5 },
                  boxShadow: "0 4px 20px rgba(25, 118, 210, 0.08)",
                  border: "1px solid rgba(25, 118, 210, 0.1)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Status Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    pb: 1.5,
                    borderBottom: "1px solid rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <SettingsInputAntennaIcon
                    sx={{
                      fontSize: "20px",
                      mr: 1,
                      color: "#1976d2",
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1976d2",
                    }}
                  >
                    Device Status
                  </Typography>
                </Box>

                {/* Status Indicator */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      bgcolor: "#e3f2fd",
                      borderRadius: 4,
                      overflow: "hidden",
                      position: "relative",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${statusPercentage(status)}%`,
                        height: "100%",
                        background: `linear-gradient(90deg, ${getStatusColor()}99, ${getStatusColor()})`,
                        transition: "width 0.5s ease",
                      }}
                    />
                  </Box>

                  {/* Status Labels */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    {["NOT READY", "READY", "CONNECTED"].map((text, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        sx={{
                          color: status === text ? getStatusColor() : "#90a4ae",
                          fontWeight: status === text ? 700 : 500,
                          fontSize: "0.7rem",
                          textAlign: "center",
                          flex: 1,
                        }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Box>

                  {/* Current Status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1.5,
                      bgcolor: "rgba(25, 118, 210, 0.05)",
                      borderRadius: 2,
                      border: `1px solid ${getStatusColor()}40`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: getStatusColor(),
                        mr: 1.5,
                        boxShadow: `0 0 0 3px ${getStatusColor()}20`,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: getStatusColor(),
                        fontSize: "0.9rem",
                        textTransform: "uppercase",
                      }}
                    >
                      {status}
                    </Typography>
                  </Box>
                </Box>

                {/* Scan Quality Indicator */}
                {scanQuality && (
                  <Box
                    sx={{
                      mt: "auto",
                      p: 2,
                      bgcolor: "rgba(25, 118, 210, 0.05)",
                      borderRadius: 2,
                      border: "1px solid rgba(25, 118, 210, 0.1)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#1976d2",
                        fontWeight: 600,
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <GradeIcon
                        sx={{ fontSize: "16px", mr: 0.5, color: "#1976d2" }}
                      />
                      Scan Quality
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={parseInt(scanQuality) || 0}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        width: "100%",
                        mb: 1.5,
                        bgcolor: "rgba(25, 118, 210, 0.2)",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #1976d2, #42a5f5)",
                          borderRadius: 3,
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        label={`${scanQuality}%`}
                        sx={{
                          bgcolor: "rgba(25, 118, 210, 0.1)",
                          color: "#1976d2",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                        }}
                        size="small"
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#546e7a",
                          fontWeight: 500,
                        }}
                      >
                        {parseInt(scanQuality) >= 80
                          ? "Excellent"
                          : parseInt(scanQuality) >= 60
                          ? "Good"
                          : parseInt(scanQuality) >= 40
                          ? "Fair"
                          : "Poor"}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Device Connection Status */}
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      bgcolor: rdDevice
                        ? "rgba(76, 175, 80, 0.05)"
                        : "rgba(117, 117, 117, 0.05)",
                      borderRadius: 2,
                      border: `1px solid ${
                        rdDevice
                          ? "rgba(76, 175, 80, 0.2)"
                          : "rgba(117, 117, 117, 0.2)"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: rdDevice ? "#4caf50" : "#757575",
                        mr: 1.5,
                        position: "relative",
                        "&::after": rdDevice
                          ? {
                              content: '""',
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              bgcolor: "#fff",
                            }
                          : {},
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: rdDevice ? "#2e7d32" : "#616161",
                        fontWeight: 600,
                      }}
                    >
                      {rdDevice ? "Device Connected" : "No Device Connected"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Form Controls */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  bgcolor: "#f8fbff",
                  borderRadius: 2,
                  p: { xs: 2, sm: 2.5 },
                  boxShadow: "0 4px 20px rgba(25, 118, 210, 0.08)",
                  border: "1px solid rgba(25, 118, 210, 0.1)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Form Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                    pb: 1.5,
                    borderBottom: "1px solid rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <BadgeIcon
                    sx={{ fontSize: "20px", mr: 1, color: "#1976d2" }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#1976d2",
                    }}
                  >
                    Authentication Details
                  </Typography>
                </Box>

                {/* Form Fields */}
                <Stack spacing={2.5} sx={{ flex: 1 }}>
                  {/* Aadhaar Input */}
                  <Box>
                    <TextField
                      label="Aadhaar Number"
                      value={aadhaar}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        if (val?.length <= 12) setAadhaar(val);
                      }}
                      size="small"
                      fullWidth
                      error={aadhaar?.length > 0 && aadhaar?.length !== 12}
                      helperText={
                        aadhaar?.length > 0 && aadhaar?.length !== 12
                          ? "Aadhaar must be 12 digits"
                          : "Enter your 12-digit Aadhaar number"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: "#1976d2" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                        },
                      }}
                      inputProps={{
                        maxLength: 12,
                        inputMode: "numeric",
                      }}
                    />
                  </Box>

                  {/* Device Selection */}
                  <Box>
                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="Select RD Device"
                      value={rdDevice ? rdDevice.info : ""}
                      onChange={(e) => {
                        const selectedDevice = rdDeviceList.find(
                          (d) => d.info === e.target.value
                        );
                        setRdDevice(selectedDevice);
                        setStatus(
                          selectedDevice ? selectedDevice.status : "NOT READY"
                        );
                      }}
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Devices sx={{ color: "#1976d2" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                        },
                      }}
                    >
                      <option value="">-- Select Device --</option>
                      {rdDeviceList.map((d, i) => (
                        <option key={i} value={d.info}>
                          {d.info} ({d.status})
                        </option>
                      ))}
                    </TextField>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#90a4ae",
                        mt: 1,
                        display: "block",
                        ml: 1,
                      }}
                    >
                      {rdDeviceList.length} device(s) detected
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 1, sm: 2 }}
                    sx={{ mt: "auto", pt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={detectDevice}
                      disabled={loading}
                      startIcon={<SearchIcon />}
                      fullWidth={window.innerWidth < 600}
                      sx={{
                        borderRadius: 2,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#1976d2",
                        borderColor: "#1976d2",
                        "&:hover": {
                          borderColor: "#1565c0",
                          bgcolor: "rgba(25, 118, 210, 0.04)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Detect Devices
                    </Button>
                    <Button
                      variant="contained"
                      onClick={startScan}
                      disabled={
                        !rdDevice ||
                        !aadhaar ||
                        aadhaar.length !== 12 ||
                        loading
                      }
                      startIcon={<FingerprintIcon />}
                      fullWidth={window.innerWidth < 600}
                      sx={{
                        borderRadius: 2,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
                          boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          background: "#e0e0e0",
                          color: "#9e9e9e",
                        },
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading ? (
                        <>
                          <CircularProgress
                            size={18}
                            sx={{ color: "white", mr: 1 }}
                          />
                          Scanning...
                        </>
                      ) : (
                        "Start Fingerprint Scan"
                      )}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {/* Information Note */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "rgba(25, 118, 210, 0.05)",
              borderRadius: 2,
              border: "1px solid rgba(25, 118, 210, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              <InfoOutlinedIcon
                sx={{ fontSize: "18px", mr: 1.5, color: "#1976d2", mt: 0.25 }}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1976d2",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                    fontSize: "0.8rem",
                  }}
                >
                  Important Instructions
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#546e7a",
                    fontSize: "0.75rem",
                    lineHeight: 1.5,
                  }}
                >
                  1. Ensure your fingerprint scanner is properly connected
                  before starting
                  <br />
                  2. Clean your finger before scanning for better quality
                  <br />
                  3. Keep your finger steady on the scanner during the process
                  <br />
                  4. Status will turn{" "}
                  <strong style={{ color: "#1976d2" }}>blue</strong> when ready
                  and <strong style={{ color: "#4caf50" }}>green</strong> when
                  connected
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
export default AEPSMERCHANTBIOMETRICKYCIPAY;
